import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaClock, FaUsers, FaCalendarAlt, FaGraduationCap } from 'react-icons/fa';
import './Services.css';
import { Helmet } from 'react-helmet';

const CoachingPrograms = () => {
  const programs = [
    {
      // icon: '🏸',
      title: 'Beginner Program',
      subtitle: 'For complete beginners',
      details: [
        { icon: <FaClock />, title: 'Duration', desc: '3 months foundation course' },
        { icon: <FaUsers />, title: 'Batch Size', desc: '8-10 students per batch' },
        { icon: <FaCalendarAlt />, title: 'Schedule', desc: '3 sessions per week, 1.5 hours each' },
        { icon: <FaGraduationCap />, title: 'Focus', desc: 'Grip, stance, basic strokes, rules' },
      ],
      price: '5,000',
      period: '/month'
    },
    {
      title: 'Intermediate Program',
      subtitle: 'For players with basic knowledge',
      details: [
        { icon: <FaClock />, title: 'Duration', desc: '6 months advancement course' },
        { icon: <FaUsers />, title: 'Batch Size', desc: '6-8 students per batch' },
        { icon: <FaCalendarAlt />, title: 'Schedule', desc: '4 sessions per week, 2 hours each' },
        { icon: <FaGraduationCap />, title: 'Focus', desc: 'Advanced strokes, footwork, match play' },
      ],
      price: '7,500',
      period: '/month'
    },
    {
      title: 'Advanced Program',
      subtitle: 'For competitive players',
      details: [
        { icon: <FaClock />, title: 'Duration', desc: '12 months elite training' },
        { icon: <FaUsers />, title: 'Batch Size', desc: '4-6 students per batch' },
        { icon: <FaCalendarAlt />, title: 'Schedule', desc: '5 sessions per week, 2.5 hours each' },
        { icon: <FaGraduationCap />, title: 'Focus', desc: 'High-performance, tournament prep, analysis' },
      ],
      price: '12,000',
      period: '/month'
    },
    {
      title: 'Professional Training',
      subtitle: 'One-on-one elite coaching',
      details: [
        { icon: <FaClock />, title: 'Duration', desc: 'Customized as per goals' },
        { icon: <FaUsers />, title: 'Batch Size', desc: 'Personal 1-on-1 training' },
        { icon: <FaCalendarAlt />, title: 'Schedule', desc: 'Flexible, 6 sessions per week' },
        { icon: <FaGraduationCap />, title: 'Focus', desc: 'Complete professional development' },
      ],
      price: '25,000',
      period: '/month'
    },
    {
      title: 'Kids Program (5-8 years)',
      subtitle: 'Early development through play',
      details: [
        { icon: <FaClock />, title: 'Duration', desc: 'Ongoing program' },
        { icon: <FaUsers />, title: 'Batch Size', desc: '10-12 kids per batch' },
        { icon: <FaCalendarAlt />, title: 'Schedule', desc: '2 sessions per week, 1 hour each' },
        { icon: <FaGraduationCap />, title: 'Focus', desc: 'Fun activities, motor skills, coordination' },
      ],
      price: '3,500',
      period: '/month'
    },
    {
      title: 'Junior Program (9-14 years)',
      subtitle: 'Structured training for juniors',
      details: [
        { icon: <FaClock />, title: 'Duration', desc: 'Ongoing with assessments' },
        { icon: <FaUsers />, title: 'Batch Size', desc: '8-10 juniors per batch' },
        { icon: <FaCalendarAlt />, title: 'Schedule', desc: '3 sessions per week, 1.5 hours each' },
        { icon: <FaGraduationCap />, title: 'Focus', desc: 'Technical skills, fitness, junior tournaments' },
      ],
      price: '5,500',
      period: '/month'
    },
  ];

  const schedule = [
    { batch: 'Early Morning', time: '6:00 AM - 7:30 AM', days: 'Mon, Wed, Fri', level: 'All Levels' },
    { batch: 'Morning', time: '7:30 AM - 9:00 AM', days: 'Mon - Sat', level: 'Intermediate & Advanced' },
    { batch: 'Kids Batch', time: '4:00 PM - 5:00 PM', days: 'Tue, Thu, Sat', level: 'Kids (5-12)' },
    { batch: 'Evening', time: '5:00 PM - 6:30 PM', days: 'Mon - Sat', level: 'Beginners' },
    { batch: 'Late Evening', time: '6:30 PM - 8:00 PM', days: 'Mon - Sat', level: 'Intermediate' },
    { batch: 'Night Batch', time: '8:00 PM - 9:30 PM', days: 'Mon - Fri', level: 'Working Professionals' },
    { batch: 'Weekend Special', time: '8:00 AM - 11:00 AM', days: 'Sat, Sun', level: 'All Levels' },
  ];

  return (
    <>
      <Helmet>
        <title>Coaching Programs</title>
      </Helmet>
      <div className="coaching-programs-page">
        <div className="page-header">
          <div className="container">
            <h1>Coaching Programs</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/services">Services</Link>
              <span>/</span>
              <span>Coaching Programs</span>
            </div>
          </div>
        </div>

        <section className="programs-section section">
          <div className="container">
            <div className="section-title">
              <h2>Training Programs for Every Level</h2>
              <p>
                Whether you're picking up a racket for the first time or training for national
                championships, we have a program designed for you.
              </p>
            </div>
            <div className="programs-grid">
              {programs.map((program, index) => (
                <div key={index} className="program-card">
                  <div className="program-header">
                    <div className="program-icon">{program.icon}</div>
                    <h3>{program.title}</h3>
                    <p>{program.subtitle}</p>
                  </div>
                  <div className="program-body">
                    <div className="program-details">
                      {program.details.map((detail, idx) => (
                        <div key={idx} className="detail-item">
                          <div className="detail-icon">{detail.icon}</div>
                          <div className="detail-content">
                            <h4>{detail.title}</h4>
                            <p>{detail.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="program-price">
                      <div className="price-label">Starting from</div>
                      <span className="price-amount">₹{program.price}</span>
                      <span className="price-period">{program.period}</span>
                    </div>
                    <Link to="/contact" className="btn btn-primary" style={{ width: '100%' }}>
                      Enroll Now <FaArrowRight />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="schedule-section section bg-light">
          <div className="container">
            <div className="section-title">
              <h2>Batch Schedule</h2>
              <p>
                Flexible timings to accommodate students, professionals, and weekend players.
              </p>
            </div>
            <div className="schedule-table">
              <table>
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Timing</th>
                    <th>Days</th>
                    <th>Level</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((item, index) => (
                    <tr key={index}>
                      <td><strong>{item.batch}</strong></td>
                      <td>{item.time}</td>
                      <td>{item.days}</td>
                      <td>{item.level}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="services-cta section">
          <div className="container">
            <div className="cta-box">
              <h2>Ready to Start Your Training?</h2>
              <p>
                Book a free trial session to experience our coaching methodology
                and find the perfect program for you.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-accent">
                  Book Free Trial <FaArrowRight />
                </Link>
                <a href="tel:+919876543210" className="btn btn-secondary">
                  Call: +91 98765 43210
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CoachingPrograms;
